import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Rocket } from "lucide-react";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function plusDays(iso, days) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export default function BoostProductDialog({ product, walletBalance, onCreated }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(plusDays(todayIso(), 6));
  const [autoRenew, setAutoRenew] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/boost-config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
    setStart(todayIso());
    setEnd(plusDays(todayIso(), 6));
    setAutoRenew(false);
  }, [open]);

  const days = (() => {
    try {
      const a = new Date(start + "T00:00:00Z"), b = new Date(end + "T00:00:00Z");
      return Math.max(1, Math.round((b - a) / 86400000) + 1);
    } catch { return 1; }
  })();
  const perDay = Number(cfg?.cost_per_day || 50);
  const total = +(perDay * days).toFixed(2);
  const insufficient = walletBalance !== undefined && total > Number(walletBalance || 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/exhibitor/products/${product.id}/boost`, { start_date: start, end_date: end, auto_renew: autoRenew });
      toast.success(`Boost live from ${start} to ${end}${autoRenew ? " — auto renew ON" : ""}. New wallet balance ₹${data.wallet_balance.toLocaleString("en-IN")}.`);
      setOpen(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create boost");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`boost-btn-${product.id}`}
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-[#F48E2E] hover:text-[#D97815] flex items-center gap-1 font-medium"
      >
        <Rocket className="h-3 w-3" /> Boost
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="boost-dialog">
          <DialogHeader><DialogTitle>Boost this product</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-600 mb-3">
            Boosted products appear at the top of category pages and search results with a Sponsored label until the end date.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Start date</div>
              <Input data-testid="boost-start" type="date" value={start} min={todayIso()} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">End date</div>
              <Input data-testid="boost-end" type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
          <div className="mt-4 p-4 rounded-2xl bg-[#FEF3E6] border border-[#F48E2E]/30">
            <div className="flex items-center justify-between text-sm">
              <span>Cost per day</span><span className="font-mono-tab font-bold">₹{perDay.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Days</span><span className="font-mono-tab font-bold" data-testid="boost-days">{days}</span>
            </div>
            <div className="mt-2 pt-2 border-t border-[#F48E2E]/20 flex items-center justify-between">
              <span className="font-medium">Total charge</span>
              <span className="font-display text-2xl text-[#F48E2E]" data-testid="boost-total">₹{total.toLocaleString("en-IN")}</span>
            </div>
            {walletBalance !== undefined && (
              <div className="mt-2 text-[11px] text-gray-600">
                Wallet balance <b>₹{Number(walletBalance).toLocaleString("en-IN")}</b>
                {insufficient && <span className="ml-2 text-red-600 font-medium">Insufficient — please recharge your wallet.</span>}
              </div>
            )}
          </div>
          <label className="mt-4 flex items-start gap-2 p-3 rounded-2xl border border-gray-200 bg-white cursor-pointer">
            <input
              data-testid="boost-auto-renew"
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              className="mt-0.5 accent-[#F48E2E]"
            />
            <div>
              <div className="text-sm font-medium">Keep renewing until my wallet runs dry</div>
              <div className="text-[11px] text-gray-500">When each cycle ends we'll try to buy another {days}-day slot at the current rate. You'll be notified if there isn't enough balance.</div>
            </div>
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="boost-submit"
              disabled={busy || insufficient}
              onClick={submit}
              className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] disabled:opacity-50"
            >
              {insufficient ? "Recharge wallet to boost" : busy ? "Boosting…" : `Pay ₹${total.toLocaleString("en-IN")} & Boost`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
